import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";
import { Upload, Car, Plus, X } from "lucide-react";

import { useSearchParams } from "react-router-dom";
import {
  getVehicleMakes,
  getVehicleModels,
  getVehicleVariants,
  getVehicleSpecifications,
  getUpcomingAuctions,
  addVehicle,
  uploadVehicleImages,
  getVehicleById,
  updateVehicle,
  requestVehicleApproval,
} from "../../services/vehicleServices.ts";
import api from "../../lib/api.ts";

const AddVehicles = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const [searchParams] = useSearchParams();
  const editVehicleId = searchParams.get("edit");
  const isEditMode = !!editVehicleId;

  const [isLoading, setIsLoading] = useState(false);
  const [vehicleImages, setVehicleImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);

  // Form data states
  const [makes, setMakes] = useState([]);
  const [years, setYears] = useState([]);
  const [models, setModels] = useState([]);
  const [bodyTypes, setBodyTypes] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [varients, setVarients] = useState([]);
  const [transmissionTypes, setTransmissionTypes] = useState([]);
  const [drivelineTypes, setDrivelineTypes] = useState([]);
  const [exteriorColors, setExteriorColors] = useState([]);
  const [interiorColors, setInteriorColors] = useState([]);
  const [cylinders, setCylinders] = useState([]);
  const [engines, setEngines] = useState([]);
  const [damageTypes, setDamageTypes] = useState([]);
  const [seatTypes, setSeatTypes] = useState([]);
  const [auctions, setAuctions] = useState([]);

  // Custom dropdown states
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [selectedValues, setSelectedValues] = useState({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm();

  const selectedMake = watch("makeId");
  const selectedModel = watch("modelId");
  const vinNumber = watch("vinNumber");
  const primaryDamageTypeId = watch("primaryDamageTypeId");
  const secondaryDamageTypeId = watch("secondaryDamageTypeId");
  const seatTypeId = watch("seatTypeId");

  // Function to generate vehicle name from make and model names
  const generateVehicleName = (year, makeId, modelId, varientId) => {
    const make = makes.find((m) => m.id === Number(makeId));
    const model = models.find((m) => m.id === Number(modelId));
    const variant = varients.find((v) => v.id === Number(varientId));

    let name = "";
    if (year) name += year + " ";
    if (make?.name) name += make.name + " ";
    if (model?.name) name += model.name + " ";
    if (variant?.name) name += variant.name;

    return name.trim() || "Vehicle";
  };

  // Custom dropdown helper functions
  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [dropdownName]: !prev[dropdownName],
    }));
  };

  const selectValue = (dropdownName, value, id, imageUrl, color) => {
    setSelectedValues((prev) => ({
      ...prev,
      [dropdownName]: { value, id, imageUrl, color },
    }));
    setOpenDropdowns((prev) => ({
      ...prev,
      [dropdownName]: false,
    }));
    // Update form value
    setValue(dropdownName, id);
  };

  const closeAllDropdowns = () => {
    setOpenDropdowns({});
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".custom-dropdown")) {
        closeAllDropdowns();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset form and states when switching from edit to add mode
  useEffect(() => {
    if (!isEditMode) {
      // Reset form data
      reset();

      // Reset all states
      setSelectedValues({});
      setOpenDropdowns({});
      setVehicleImages([]);
      setPreviewImages([]);

      // Reset dependent dropdowns
      setModels([]);
      setVarients([]);
    }
  }, [isEditMode, reset]);

  // Map helper by id
  const findOptionById = (options, id) => {
    if (!id) return undefined;
    return options?.find?.((o) => Number(o.id) === Number(id));
  };

  // VIN lookup and field mapping
  useEffect(() => {
    let timeoutId;

    const run = async () => {
      try {
        if (!vinNumber || String(vinNumber).length !== 17) return;
        const resp = await api.get(`/products/getinfobyvin/${vinNumber}`, {
          timeout: 30000, // 30 seconds timeout for VIN lookup
        });
        const data = resp?.data?.data || resp?.data;
        console.log("VIN info:", data);
        if (!data) return;

        // Basic
        if (data.year) setValue("year", Number(data.year));

        // Map ids coming from API
        if (data.make) {
          setValue("makeId", Number(data.make));
          const op = findOptionById(makes, data.make);
          if (op)
            setSelectedValues((p) => ({
              ...p,
              makeId: { value: op.name, id: op.id },
            }));
        }
        if (data.model) {
          setValue("modelId", Number(data.model));
          const op = findOptionById(models, data.model);
          if (op)
            setSelectedValues((p) => ({
              ...p,
              modelId: { value: op.name, id: op.id },
            }));
        }
        if (data.trim) {
          setValue("varientId", Number(data.trim));
          const op = findOptionById(varients, data.trim);
          if (op)
            setSelectedValues((p) => ({
              ...p,
              varientId: { value: op.name, id: op.id },
            }));
        }
        if (data.bodyClass) {
          setValue("bodyTypeId", Number(data.bodyClass));
          const op = findOptionById(bodyTypes, data.bodyClass);
          if (op)
            setSelectedValues((p) => ({
              ...p,
              bodyTypeId: { value: op.name, id: op.id },
            }));
        }
        if (data.transmissionStyle) {
          setValue("transmissionTypeId", Number(data.transmissionStyle));
          const op = findOptionById(transmissionTypes, data.transmissionStyle);
          if (op)
            setSelectedValues((p) => ({
              ...p,
              transmissionTypeId: { value: op.name, id: op.id },
            }));
        }
        if (data.driveType) {
          setValue("driveLineTypeId", Number(data.driveType));
          const op = findOptionById(drivelineTypes, data.driveType);
          if (op)
            setSelectedValues((p) => ({
              ...p,
              driveLineTypeId: { value: op.name, id: op.id },
            }));
        }
        if (data.fuelType) {
          setValue("fuelTypeId", Number(data.fuelType));
          const op = findOptionById(fuelTypes, data.fuelType);
          if (op)
            setSelectedValues((p) => ({
              ...p,
              fuelTypeId: { value: op.name, id: op.id },
            }));
        }
        if (data.engineCylinders) {
          setValue("cylinderId", Number(data.engineCylinders));
          const op = findOptionById(cylinders, data.engineCylinders);
          if (op)
            setSelectedValues((p) => ({
              ...p,
              cylinderId: { value: op.name, id: op.id },
            }));
        }
        if (data.engineCylinders) {
          // Only map engineId from VIN if a matching Engine record actually exists
          const engineOp = findOptionById(engines, data.engineCylinders);
          if (engineOp) {
            setValue("engineId", Number(engineOp.id));
            setSelectedValues((p) => ({
              ...p,
              engineId: { value: engineOp.name, id: engineOp.id },
            }));
          }
        }
      } catch (e) {
        console.log("VIN map error", e?.response?.data || e?.message);
      }
    };

    // Clear existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Set new timeout for debounced execution
    if (vinNumber && String(vinNumber).length === 17) {
      timeoutId = setTimeout(run, 500); // 0.5 second delay
    }

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vinNumber,
    makes,
    models,
    varients,
    bodyTypes,
    transmissionTypes,
    drivelineTypes,
    fuelTypes,
    cylinders,
  ]);

  // Ensure edit-mode selected labels appear after options load
  useEffect(() => {
    if (damageTypes?.length && primaryDamageTypeId) {
      const op = damageTypes.find(
        (d) => Number(d.id) === Number(primaryDamageTypeId)
      );
      if (op) {
        setSelectedValues((prev) => ({
          ...prev,
          primaryDamageTypeId: {
            value: op.name,
            id: op.id,
            imageUrl: op.imageUrl,
          },
        }));
      }
    }
    if (damageTypes?.length && secondaryDamageTypeId) {
      const op = damageTypes.find(
        (d) => Number(d.id) === Number(secondaryDamageTypeId)
      );
      if (op) {
        setSelectedValues((prev) => ({
          ...prev,
          secondaryDamageTypeId: {
            value: op.name,
            id: op.id,
            imageUrl: op.imageUrl,
          },
        }));
      }
    }
  }, [damageTypes, primaryDamageTypeId, secondaryDamageTypeId]);

  useEffect(() => {
    if (seatTypes?.length && seatTypeId) {
      const op = seatTypes.find((s) => Number(s.id) === Number(seatTypeId));
      if (op) {
        setSelectedValues((prev) => ({
          ...prev,
          seatTypeId: { value: op.name, id: op.id, imageUrl: op.imageUrl },
        }));
      }
    }
  }, [seatTypes, seatTypeId]);

  // Custom Dropdown Component
  const CustomDropdown = ({
    name,
    label,
    options,
    placeholder,
    required = false,
    error = null,
  }) => {
    const isOpen = openDropdowns[name] || false;
    const selected = selectedValues[name];

    

    // Check if this dropdown is for colors (exterior or interior)
    const isColorDropdown =
      name === "exteriorColorId" || name === "interiorColorId";

    return (
      <div className='custom-dropdown'>
        <label className='block text-sm font-medium text-white/70 mb-2'>
          {label} {required && <span className='text-red-500'>*</span>}
        </label>
        <div className='relative'>
          <button
            type="button"
            onClick={() => toggleDropdown(name)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-white focus:border-white bg-page text-left flex items-center justify-between transition-colors ${error ? "border-red-300" : "border-default bg-page text-white"
              } ${isOpen ? "border-white" : ""}`}
          >
            <div className="flex items-center gap-2 min-h-[20px]">
              {selected?.imageUrl ? (
                <img
                  src={selected.imageUrl}
                  alt={selected.value}
                  className="w-6 h-6 object-cover rounded border border-gray-200 bg-white"
                  onError={(e) => (e.target.style.display = "none")}
                />
              ) : selected?.color ? (
                <div
                  className="w-6 h-6 rounded-full border border-default shadow-sm"
                  style={{ backgroundColor: selected.color }}
                  title={`${selected.value} (${selected.color})`}
                />
              ) : null}

              <span className={selected ? "text-white" : "text-white/70"}>
                {selected ? selected.value : placeholder}
              </span>
            </div>

            <svg
              className={`w-4 h-4 transition-transform text-gray-400 ${isOpen ? "rotate-180" : ""
                }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isOpen && (
            <div className='absolute z-10 w-full mt-1 bg-page border border-default bg-page text-white rounded-lg shadow-lg max-h-60 overflow-y-auto'>
              {options.map((option) => (
                <button
                  key={option.id}
                  type='button'
                  onClick={() =>
                    selectValue(
                      name,
                      option.name,
                      option.id,
                      option.imageUrl,
                      option.bgColor
                    )
                  }
                  className='w-full px-3 py-2 text-left hover:bg-primary flex items-center gap-2 border-b border-default last:border-b-0 transition-colors'
                >
                  {option.imageUrl ? (
                    <img
                      src={option.imageUrl}
                      alt={option.name}
                      className='w-6 h-6 object-cover rounded border border-gray-200  bg-white'
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : option.bgColor && (
                    <div
                      className='w-6 h-6 rounded-full border border-default bg-page text-white flex items-center justify-center shadow-sm relative group'
                      style={{ backgroundColor: option.bgColor }}
                      title={`${option.name} (${option.bgColor})`}
                    >
                      <div className='absolute inset-0 rounded-full border-2 border-white opacity-0 group-hover:opacity-100 transition-opacity'></div>
                    </div>
                  )}
                  <span className='text-white/70'>{option.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {error && <p className='mt-1 text-sm text-red-600'>{error.message}</p>}
        {/* Hidden input for form validation */}
        <input
          type='hidden'
          {...register(
            name,
            required ? { required: `${label} is required` } : {}
          )}
        />
      </div>
    );
  };

  // Show alert function - now uses global toast system
  const showAlert = (message, type = "success") => {
    if (type === "success") {
      showSuccess(message);
    } else {
      showError(message);
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch vehicle data if in edit mode (after initial data is loaded)
  useEffect(() => {
    if (isEditMode && editVehicleId && auctions.length > 0) {
      fetchVehicleData();
    }
  }, [isEditMode, editVehicleId, auctions]);

  // Fetch models when make changes
  useEffect(() => {
    if (selectedMake) {
      fetchModels(selectedMake);
    }
  }, [selectedMake]);

  // Fetch variants when model changes
  useEffect(() => {
    if (selectedModel) {
      fetchVariants(selectedModel);
    }
  }, [selectedModel]);

  const fetchInitialData = async () => {
    try {
      const [makesData, specificationsData, auctionsData] = await Promise.all([
        getVehicleMakes(),
        getVehicleSpecifications(),
        getUpcomingAuctions(),
      ]);

      setMakes(Array.isArray(makesData) ? makesData : []);

      setBodyTypes(specificationsData.bodyTypes || []);
      setFuelTypes(specificationsData.fuelTypes || []);
      setSpecifications(specificationsData.specifications || []);
      setHighlights(specificationsData.highlights || []);
      setTransmissionTypes(specificationsData.transmissionTypes || []);
      setDrivelineTypes(specificationsData.drivelineTypes || []);
      setExteriorColors(specificationsData.exteriorColors || []);
      setInteriorColors(specificationsData.interiorColors || []);
      setSeatTypes(specificationsData.seatTypes || []);
      setCylinders(specificationsData.cylinders || []);
      setDamageTypes(specificationsData.damageTypes || []);
      setEngines(specificationsData.engines || []);
      setAuctions(auctionsData?.data || []);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to load form data. Please refresh the page.";
      showAlert(errorMessage, "error");
    }
  };

  const fetchModels = async (makeId) => {
    try {
      const modelsData = await getVehicleModels(makeId);
      setModels(Array.isArray(modelsData) ? modelsData : []);
      // setValue("modelId"); // Reset model selection
    } catch (error) {
      console.error("Error fetching models:", error);
      setModels([]);
    }
  };

  const fetchVariants = async (modelId) => {
    try {
      const variantsData = await getVehicleVariants(modelId);
      setVarients(Array.isArray(variantsData) ? variantsData : []);
      // setValue("varientId"); // Reset variant selection
    } catch (error) {
      console.error("Error fetching variants:", error);
      setVarients([]);
    }
  };

  // Functions for edit mode that don't reset selections
  const fetchModelsForEdit = async (makeId) => {
    try {
      const modelsData = await getVehicleModels(makeId);
      setModels(Array.isArray(modelsData) ? modelsData : []);
      // Don't reset model selection in edit mode
    } catch (error) {
      console.error("Error fetching models:", error);
      setModels([]);
    }
  };

  const fetchVariantsForEdit = async (modelId) => {
    try {
      const variantsData = await getVehicleVariants(modelId);
      setVarients(Array.isArray(variantsData) ? variantsData : []);
      // Don't reset variant selection in edit mode
    } catch (error) {
      console.error("Error fetching variants:", error);
      setVarients([]);
    }
  };

  const fetchVehicleData = async () => {
    try {
      setIsLoading(true);
      const vehicleData = await getVehicleById(editVehicleId);
      console.log("vehicleData", vehicleData);
      // First fetch dependent data (models, variants) based on make/model
      if (vehicleData.MakeModel?.make?.id) {
        await fetchModelsForEdit(vehicleData.MakeModel.make.id);
      }
      if (vehicleData.MakeModel?.id) {
        await fetchVariantsForEdit(vehicleData.MakeModel.id);
      }

      // Now populate form with existing data after dependent data is loaded
      setValue("makeId", vehicleData.MakeModel?.make?.id || null);
      setValue("modelId", vehicleData.MakeModel?.id || null);
      setValue("varientId", vehicleData.MakeModel?.id || null);
      setValue("vinNumber", vehicleData.vinNumber);
      setValue("mileage", vehicleData.mileage);
      setValue("reservedPrice", vehicleData.reservedPrice);
      setValue("marketValue", vehicleData.marketValue);
      setValue("name", vehicleData.name);
      setValue("startingBid", vehicleData.startingBid);

      // Extract auction ID and lane ID from nested structure
      let auctionId = "";
      let auctionLaneId = "";

      if (
        vehicleData.auctionLanesVehicles &&
        vehicleData.auctionLanesVehicles.length > 0
      ) {
        const auctionLaneVehicle = vehicleData.auctionLanesVehicles[0];
        auctionId = auctionLaneVehicle.auctionLane?.auctionId;
        auctionLaneId = auctionLaneVehicle.auctionLane?.laneId;
      } else {
        // Fallback to direct properties if nested structure doesn't exist
        auctionId = vehicleData.auctionId;
        auctionLaneId = vehicleData.auctionLaneId;
      }

      // Ensure IDs are properly formatted (strings for select elements)
      const formattedAuctionId = auctionId ? String(auctionId) : "";
      const formattedLaneId = auctionLaneId ? String(auctionLaneId) : "";

      setValue("auctionId", formattedAuctionId);
      setValue("auctionLaneId", formattedLaneId);
      setValue("bodyTypeId", vehicleData.bodyTypeId);
      setValue("fuelTypeId", vehicleData.fuelTypeId);
      setValue("highlightId", vehicleData.highlightId);
      setValue("specificationId", vehicleData.specificationId);
      setValue("transmissionTypeId", vehicleData.transmissionTypeId);
      setValue("driveLineTypeId", vehicleData.driveLineTypeId);
      setValue("exteriorColorId", vehicleData.exteriorColorId);
      setValue("interiorColorId", vehicleData.interiorColorId);
      setValue("cylinderId", vehicleData.cylinderId);
      setValue("engineId", vehicleData.engineId);
      setValue("horsePower", vehicleData.horsePower || 0);
      // Prefill damage types separately; fallback to single damageTypeId if provided
      if (vehicleData.primaryDamageTypeId) {
        setValue("primaryDamageTypeId", vehicleData.primaryDamageTypeId);
      } else if (vehicleData.damageTypeId) {
        setValue("primaryDamageTypeId", vehicleData.damageTypeId);
      }
      if (vehicleData.secondaryDamageTypeId) {
        setValue("secondaryDamageTypeId", vehicleData.secondaryDamageTypeId);
      } else if (vehicleData.damageTypeId) {
        setValue("secondaryDamageTypeId", vehicleData.damageTypeId);
      }
      // Correct seat type field
      setValue("seatTypeId", vehicleData.seatTypeId || null);
      setValue("reservePrice", vehicleData.reservePrice);
      setValue("description", vehicleData.description);
      setValue("notes", vehicleData.notes);
      setValue("year", vehicleData.year);
      setValue("varientId", vehicleData.varientId);

      // Set selected values for custom dropdowns
      if (vehicleData.bodyTypeId) {
        const bodyType = bodyTypes.find(
          (bt) => bt.id === vehicleData.bodyTypeId
        );
        if (bodyType) {
          setSelectedValues((prev) => ({
            ...prev,
            bodyTypeId: {
              value: bodyType.name,
              id: bodyType.id,
              imageUrl: bodyType.imageUrl,
            },
          }));
        }
      }
      if (vehicleData.fuelTypeId) {
        const fuelType = fuelTypes.find(
          (ft) => ft.id === vehicleData.fuelTypeId
        );
        if (fuelType) {
          setSelectedValues((prev) => ({
            ...prev,
            fuelTypeId: {
              value: fuelType.name,
              id: fuelType.id,
              imageUrl: fuelType.imageUrl,
            },
          }));
        }
      }
      if (vehicleData.highlightId) {
        const highlight = highlights.find(
          (h) => h.id === vehicleData.highlightId
        );
        if (highlight) {
          setSelectedValues((prev) => ({
            ...prev,
            highlightId: {
              value: highlight.name,
              id: highlight.id,
              imageUrl: highlight.imageUrl,
            },
          }));
        }
      }
      if (vehicleData.specificationId) {
        const spec = specifications.find(
          (s) => s.id === vehicleData.specificationId
        );
        if (spec) {
          setSelectedValues((prev) => ({
            ...prev,
            specificationId: {
              value: spec.name,
              id: spec.id,
              imageUrl: spec.imageUrl,
            },
          }));
        }
      }
      if (vehicleData.transmissionTypeId) {
        const transmission = transmissionTypes.find(
          (t) => t.id === vehicleData.transmissionTypeId
        );
        if (transmission) {
          setSelectedValues((prev) => ({
            ...prev,
            transmissionTypeId: {
              value: transmission.name,
              id: transmission.id,
              imageUrl: transmission.imageUrl,
            },
          }));
        }
      }
      if (vehicleData.driveLineTypeId) {
        const driveline = drivelineTypes.find(
          (d) => d.id === vehicleData.driveLineTypeId
        );
        if (driveline) {
          setSelectedValues((prev) => ({
            ...prev,
            driveLineTypeId: {
              value: driveline.name,
              id: driveline.id,
              imageUrl: driveline.imageUrl,
            },
          }));
        }
      }
      if (vehicleData.exteriorColorId) {
        const color = exteriorColors.find(
          (c) => c.id === vehicleData.exteriorColorId
        );
        if (color) {
          setSelectedValues((prev) => ({
            ...prev,
            exteriorColorId: {
              value: color.name,
              id: color.id,
              imageUrl: color.imageUrl,
              color: color.color,
            },
          }));
        }
      }
      if (vehicleData.interiorColorId) {
        const color = interiorColors.find(
          (c) => c.id === vehicleData.interiorColorId
        );
        if (color) {
          setSelectedValues((prev) => ({
            ...prev,
            interiorColorId: {
              value: color.name,
              id: color.id,
              imageUrl: color.imageUrl,
              color: color.color,
            },
          }));
        }
      }
      if (vehicleData.cylinderId) {
        const cylinder = cylinders.find((c) => c.id === vehicleData.cylinderId);
        if (cylinder) {
          setSelectedValues((prev) => ({
            ...prev,
            cylinderId: {
              value: cylinder.name,
              id: cylinder.id,
              imageUrl: cylinder.imageUrl,
            },
          }));
        }
      }
      if (vehicleData.engineId) {
        const engine = engines.find((e) => e.id === vehicleData.engineId);
        if (engine) {
          setSelectedValues((prev) => ({
            ...prev,
            engineId: {
              value: engine.name,
              id: engine.id,
              imageUrl: engine.imageUrl,
            },
          }));
        }
      }

      // Primary Damage Type selected label
      if (vehicleData.primaryDamageTypeId || vehicleData.damageTypeId) {
        const effectivePrimary =
          vehicleData.primaryDamageTypeId ?? vehicleData.damageTypeId;
        const primaryDamage = damageTypes.find(
          (d) => Number(d.id) === Number(effectivePrimary)
        );
        if (primaryDamage) {
          setSelectedValues((prev) => ({
            ...prev,
            primaryDamageTypeId: {
              value: primaryDamage.name,
              id: primaryDamage.id,
              imageUrl: primaryDamage.imageUrl,
            },
          }));
        }
      }

      // Secondary Damage Type selected label
      if (vehicleData.secondaryDamageTypeId || vehicleData.damageTypeId) {
        const effectiveSecondary =
          vehicleData.secondaryDamageTypeId ?? vehicleData.damageTypeId;
        const secondaryDamage = damageTypes.find(
          (d) => Number(d.id) === Number(effectiveSecondary)
        );
        if (secondaryDamage) {
          setSelectedValues((prev) => ({
            ...prev,
            secondaryDamageTypeId: {
              value: secondaryDamage.name,
              id: secondaryDamage.id,
              imageUrl: secondaryDamage.imageUrl,
            },
          }));
        }
      }

      // Set existing images
      if (vehicleData.images && vehicleData.images.length > 0) {
        setPreviewImages(vehicleData.images.map((url) => ({ preview: url })));
      }
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to load vehicle data for editing";
      showAlert(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      if (file.size > 5 * 1024 * 1024) {
        showAlert("File size should be less than 5MB", "error");
        return false;
      }
      if (!file.type.startsWith("image/")) {
        showAlert("Please select only image files", "error");
        return false;
      }
      return true;
    });

    if (validFiles.length + vehicleImages.length > 10) {
      showAlert("Maximum 10 images allowed", "error");
      return;
    }

    setVehicleImages((prev) => [...prev, ...validFiles]);

    // Create preview URLs
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImages((prev) => [
          ...prev,
          { file, preview: e.target.result },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    // Check if this is an existing image (URL) or new upload (base64)
    const imageToRemove = previewImages[index];
    const isExistingImage =
      typeof imageToRemove === "string" ||
      (imageToRemove.preview &&
        !imageToRemove.preview.startsWith("data:image"));

    if (isExistingImage) {
      // Remove existing image from preview
      setPreviewImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      // Remove new uploaded image
      const newImageIndex =
        index -
        previewImages.filter((item) => {
          const preview = typeof item === "string" ? item : item.preview;
          return preview && !preview.startsWith("data:image");
        }).length;

      if (newImageIndex >= 0) {
        setVehicleImages((prev) => prev.filter((_, i) => i !== newImageIndex));
      }
      setPreviewImages((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleFormSubmit = async (data) => {
    const authUser = JSON.parse(localStorage.getItem("authUser"));
    if (!authUser || !authUser.id) {
      showAlert("Please login to add vehicles", "error");
      return;
    }

    if (vehicleImages.length === 0 && previewImages.length === 0) {
      showAlert("Please upload at least one vehicle image", "error");
      return;
    }

    setIsLoading(true);

    try {
      // 1. Upload images to S3 first to get URLs
      const imageUrls = [];

      // If editing, start with existing images (only URLs, not base64 data)
      if (isEditMode && previewImages.length > 0) {
        const existingImageUrls = previewImages
          .filter((item) => {
            // Only include items that are actual URLs (not base64 data)
            const preview = typeof item === "string" ? item : item.preview;
            return preview && !preview.startsWith("data:image");
          })
          .map((item) => {
            const preview = typeof item === "string" ? item : item.preview;
            return preview;
          });
        imageUrls.push(...existingImageUrls);
      }

      // Upload new images if any (only actual File objects, not base64)
      if (vehicleImages.length > 0) {
        try {
          const formData = new FormData();

          // Only append actual File objects, not base64 data
          vehicleImages.forEach((image) => {
            if (image instanceof File) {
              formData.append("images", image);
            }
          });

          formData.append("uploadPath", "vehicles"); // Upload to vehicles folder

          // Get auth user and branch from localStorage
          const authUser = localStorage.getItem("authUser");
          const branch = localStorage.getItem("branch");
          const parsedAuthUser = JSON.parse(authUser);

          // Upload images to S3
          const imageResponse = await api.post(`/image/upload`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${parsedAuthUser.token}`,
              "x-branch": branch,
            },
          });

          // Extract image URLs from response
          const uploadedImageUrls =
            imageResponse?.data?.imageUrls || imageResponse?.imageUrls || [];
          imageUrls.push(...uploadedImageUrls);
        } catch (imageError) {
          console.error("Error uploading images to S3:", imageError);
          const errorMessage =
            imageError.response?.data?.message ||
            imageError.response?.data?.error ||
            imageError.message ||
            "Failed to upload images. Please try again.";
          showAlert(errorMessage, "error");
          setIsLoading(false);
          return;
        }
      }

      // 2. Build payload with S3 image URLs

      const payload = {
        makeId: Number(data?.makeId),
        modelId: Number(data.modelId),
        vinNumber: data?.vinNumber,
        mileage: Number(data?.mileage),
        reservedPrice: Number(data?.reservedPrice),
        marketValue: Number(data?.marketValue),
        name: generateVehicleName(
          Number(data?.year),
          data.makeId,
          data?.modelId,
          data?.varientId
        ),
        startingBid: 0,
        horsePower: 0,

        // Helper: only include FK id if it actually exists in the loaded options list
        bodyTypeId: data?.bodyTypeId && bodyTypes.find((o) => o.id === Number(data.bodyTypeId))
          ? Number(data.bodyTypeId) : undefined,
        fuelTypeId: data?.fuelTypeId && fuelTypes.find((o) => o.id === Number(data.fuelTypeId))
          ? Number(data.fuelTypeId) : undefined,
        highlightId: data?.highlightId && highlights.find((o) => o.id === Number(data.highlightId))
          ? Number(data.highlightId) : undefined,
        specificationId: data?.specificationId && specifications.find((o) => o.id === Number(data.specificationId))
          ? Number(data.specificationId) : undefined,
        transmissionTypeId: data?.transmissionTypeId && transmissionTypes.find((o) => o.id === Number(data.transmissionTypeId))
          ? Number(data.transmissionTypeId) : undefined,
        driveLineTypeId: data?.driveLineTypeId && drivelineTypes.find((o) => o.id === Number(data.driveLineTypeId))
          ? Number(data.driveLineTypeId) : undefined,
        exteriorColorId: data?.exteriorColorId && exteriorColors.find((o) => o.id === Number(data.exteriorColorId))
          ? Number(data.exteriorColorId) : undefined,
        interiorColorId: data?.interiorColorId && interiorColors.find((o) => o.id === Number(data.interiorColorId))
          ? Number(data.interiorColorId) : undefined,
        cylinderId: data?.cylinderId && cylinders.find((o) => o.id === Number(data.cylinderId))
          ? Number(data.cylinderId) : undefined,
        engineId: data?.engineId && engines.find((o) => o.id === Number(data.engineId))
          ? Number(data.engineId) : undefined,
        primaryDamageTypeId: data?.primaryDamageTypeId && damageTypes.find((o) => o.id === Number(data.primaryDamageTypeId))
          ? Number(data.primaryDamageTypeId) : undefined,
        secondaryDamageTypeId: data?.secondaryDamageTypeId && damageTypes.find((o) => o.id === Number(data.secondaryDamageTypeId))
          ? Number(data.secondaryDamageTypeId) : undefined,
        seatTypeId: data?.seatTypeId && seatTypes.find((o) => o.id === Number(data.seatTypeId))
          ? Number(data.seatTypeId) : undefined,
        // auctionId: data.auctionId ? Number(data.auctionId) : undefined, // Removed - managed by admin
        reservePrice: data?.reservePrice
          ? Number(data.reservePrice)
          : undefined,
        description: data?.description || undefined,
        notes: data?.notes || undefined,
        year: Number(data.year),
        sellerId: authUser.seller.id,
        sellerType: authUser.userType,
        images: imageUrls, // <-- S3 image URLs array
      };

      // Send the vehicle data with images included in payload
      let response;
      if (isEditMode) {
        // Check if editing a rejected vehicle
        try {
          const currentVehicleData = await getVehicleById(editVehicleId);
          const currentStatus =
            currentVehicleData.ProductStatus?.name ||
            currentVehicleData.ProductStatus;

          if (currentStatus === "Rejected") {
            // For rejected vehicles:
            // Step 1 – save the updated data (new reserved price etc.)
            console.log(
              "Editing rejected vehicle - saving updated data first"
            );
            await updateVehicle(editVehicleId, payload);

            // Step 2 – request re-approval so it goes back to admin review
            console.log("Requesting re-approval for rejected vehicle");
            response = await requestVehicleApproval(editVehicleId);

            // Ensure response is treated as success regardless of shape
            if (!response) response = { success: true };
            else if (typeof response === "object" && !response.success) {
              response.success = true;
            }
          } else {
            // For other vehicles, use normal update
            response = await updateVehicle(editVehicleId, payload);
          }
        } catch (error) {
          console.error("Error updating vehicle:", error);
          const errorMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Failed to update vehicle. Please try again.";
          showAlert(errorMessage, "error");
          setIsLoading(false);
          return;
        }
      } else {
        // Create new vehicle
        try {
          response = await addVehicle(payload);
        } catch (addError) {
          console.error("Error adding vehicle:", addError);
          const errorMessage =
            addError.response?.data?.message ||
            addError.response?.data?.error ||
            addError.message ||
            "Failed to add vehicle. Please try again.";
          showAlert(errorMessage, "error");
          setIsLoading(false);
          return;
        }
      }

      if (
        response?.success ||
        response?.status === 200 ||
        response?.status === 201 ||
        response?.id ||
        response?.data?.id
      ) {
        showAlert(
          isEditMode
            ? "Vehicle updated successfully with images! Redirecting to your vehicles..."
            : "Vehicle added successfully with images! Redirecting to your vehicles...",
          "success"
        );
        reset();
        setVehicleImages([]);
        setPreviewImages([]);
        setSelectedValues({});
        setOpenDropdowns({});

        setTimeout(() => {
          window.location.href = "/myvehicles";
        }, 2000);
      } else {
        // Handle unsuccessful response
        const errorMessage =
          response?.message ||
          response?.error ||
          (isEditMode
            ? "Failed to update vehicle. Please try again."
            : "Failed to add vehicle. Please try again.");
        showAlert(errorMessage, "error");
      }
    } catch (error) {
      console.error("Error adding vehicle:", error);

      // Extract detailed error information
      let errorMessage =
        "Failed to add vehicle. Please check your input and try again.";

      if (error.response?.data) {
        const errorData = error.response.data;

        // Handle validation errors (array of field errors)
        if (Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors
            .map((err) => `${err.field || "Field"}: ${err.message || err}`)
            .join(", ");
        }
        // Handle single error message
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
        // Handle error object with details
        else if (errorData.error) {
          errorMessage = errorData.error;
        }
        // Handle validation error object
        else if (typeof errorData === "object") {
          const fieldErrors = Object.entries(errorData)
            .map(([field, message]) => `${field}: ${message}`)
            .join(", ");
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      showAlert(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className=' min-h-screen'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      <div className='max-w-4xl mx-auto '>
        {/* Header */}
        <div className='mb-2'>
          <div
            className={`flex items-center gap-3 mb-4 ${isRTL ? "flex-row-reverse" : ""
              }`}
          >
            <div className='p-2 bg-primary/10 rounded-lg'>
              <Car className='w-6 h-6 text-primary' />
            </div>
            <div>
              <h6 className=' font-bold text-white'>
                {isEditMode
                  ? translate("addVehicle.heading.edit")
                  : translate("addVehicle.heading.add")}
              </h6>
              <p className='text-white'>
                {isEditMode
                  ? translate("addVehicle.subtitle.edit")
                  : translate("addVehicle.subtitle.add")}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-6'>
          {/* Basic Vehicle Information */}
          <div className='bg-card rounded-lg shadow-sm border border-default p-6'>
            <h2 className='text-xl font-semibold text-white mb-6'>
              {translate("addVehicle.sections.basicInfo")}
            </h2>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* VIN Number */}
              <div>
                <label className='block text-sm font-medium text-white/70 mb-2'>
                  {translate("addVehicle.labels.vinNumber")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  {...register("vinNumber", {
                    required: translate("addVehicle.validation.vinRequired"),
                    minLength: {
                      value: 17,
                      message: translate("addVehicle.validation.vinLength"),
                    },
                    maxLength: {
                      value: 17,
                      message: translate("addVehicle.validation.vinLength"),
                    },
                  })}
                  onChange={(e) => {
                    // Update the form value
                    setValue("vinNumber", e.target.value);
                  }}
                  className={`w-full px-3 py-2 bg-page text-white border border-default rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                  placeholder={translate("addVehicle.placeholders.vinNumber")}
                  maxLength={17}
                />
                {errors.vinNumber && (
                  <p className='mt-1 text-sm text-red-600'>
                    {errors.vinNumber.message}
                  </p>
                )}
              </div>
              {/* Make */}
              <div>
                <label className='block text-sm font-medium text-white/70 mb-2'>
                  {translate("addVehicle.labels.make")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <select
                  {...register("makeId", {
                    required: translate("addVehicle.validation.makeRequired"),
                  })}
                  className={`w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                >
                  <option value=''>
                    {translate("addVehicle.placeholders.selectMake")}
                  </option>
                  {makes.map((make) => (
                    <option key={make.id} value={make.id}>
                      {make.name}
                    </option>
                  ))}
                </select>
                {errors.makeId && (
                  <p className='mt-1 text-sm text-red-600'>
                    {errors.makeId.message}
                  </p>
                )}
              </div>

              {/* Model */}
              <div>
                <label className='block text-sm font-medium text-white/70 mb-2'>
                  {translate("addVehicle.labels.model")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <select
                  {...register("modelId", {
                    required: translate("addVehicle.validation.modelRequired"),
                  })}
                  className={`w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                  disabled={!selectedMake}
                >
                  <option value=''>
                    {translate("addVehicle.placeholders.selectModel")}
                  </option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
                {errors.modelId && (
                  <p className='mt-1 text-sm text-red-600'>
                    {errors.modelId.message}
                  </p>
                )}
              </div>

              {/* Variant */}
              <div>
                <label className='block text-sm font-medium text-white/70 mb-2'>
                  {translate("addVehicle.labels.variant")}
                </label>
                <select
                  {...register("varientId")}
                  className={`w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                  disabled={!selectedModel}
                >
                  <option value=''>
                    {translate("addVehicle.placeholders.selectVariant")}
                  </option>
                  {varients.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className='block text-sm font-medium text-white/70 mb-2'>
                  {translate("addVehicle.labels.year")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <input
                  type='number'
                  min='1900'
                  max={new Date().getFullYear() + 1}
                  {...register("year", {
                    required: translate("addVehicle.validation.yearRequired"),
                    min: {
                      value: 1900,
                      message: translate("addVehicle.validation.yearMin"),
                    },
                    max: {
                      value: new Date().getFullYear() + 1,
                      message: translate(
                        "addVehicle.validation.yearMax"
                      ).replace("{year}", String(new Date().getFullYear() + 1)),
                    },
                  })}
                  className={`w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                  placeholder={translate("addVehicle.placeholders.year")}
                />
                {errors.year && (
                  <p className='mt-1 text-sm text-red-600'>
                    {errors.year.message}
                  </p>
                )}
              </div>
              {/* Highlight*/}
              <div>
                <CustomDropdown
                  name='highlightId'
                  label={translate("addVehicle.labels.highlight")}
                  options={highlights}
                  placeholder={translate(
                    "addVehicle.placeholders.selectHighlight"
                  )}
                  error={errors.highlightId}
                />
              </div>

              {/* Cylinder*/}
              <div>
                <CustomDropdown
                  name='cylinderId'
                  label={translate("addVehicle.labels.cylinder")}
                  options={cylinders}
                  placeholder={translate(
                    "addVehicle.placeholders.selectCylinder"
                  )}

                  error={errors.cylinderId}
                />
              </div>

              {/* Engine*/}
              <div>
                <CustomDropdown
                  name='engineId'
                  label={translate("addVehicle.labels.engine")}
                  options={engines}
                  placeholder={translate(
                    "addVehicle.placeholders.selectEngine"
                  )}
                  required={true}
                  error={errors.engineId}

                />
              </div>

              {/* Damage Type */}
              {/* <div> 
                    <label className='block text-sm font-medium text-white/70 mb-2'>
                    Primary Damage Type
                    </label>
                    <select
                      {...register("damageTypeId")}
                      className='w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary'
                    >
                      <option value=''>Select Primary Damage Type</option>
                      {damageTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                    {errors.damageTypeId && (
                      <p className='mt-1 text-sm text-red-600'>
                        {errors.damageTypeId.message}
                      </p>
                    )}
                  </div> */}
              {/* Damage Type*/}
              {/* <div> 
                    <label className='block text-sm font-medium text-white/70 mb-2'>
                    Secondary Damage Type
                    </label>
                    <select
                      {...register("damageTypeId")}
                      className='w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary'
                    >
                      <option value=''>Select Secondary Damage Type</option>
                      {damageTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                    {errors.damageTypeId && (
                      <p className='mt-1 text-sm text-red-600'>
                        {errors.damageTypeId.message}
                      </p>
                    )}
                  </div> */}

              {/* Mileage */}
              <div>
                <label className='block text-sm font-medium text-white/70 mb-2'>
                  {translate("addVehicle.labels.mileage")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <input
                  type='number'
                  min='0'
                  {...register("mileage", {
                    required: translate(
                      "addVehicle.validation.mileageRequired"
                    ),
                    min: {
                      value: 0,
                      message: translate("addVehicle.validation.mileageMin"),
                    },
                  })}
                  className={`w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                  placeholder={translate("addVehicle.placeholders.mileage")}
                />
                {errors.mileage && (
                  <p className='mt-1 text-sm text-red-600'>
                    {errors.mileage.message}
                  </p>
                )}
              </div>

              {/* Reserved Price */}
              <div>
                <label className='block text-sm font-medium text-white/70 mb-2'>
                  {translate("addVehicle.labels.reservedPrice")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <input
                  type='number'
                  min='0'
                  step='0.01'
                  {...register("reservedPrice", {
                    required: translate(
                      "addVehicle.validation.reservedPriceRequired"
                    ),
                    min: {
                      value: 0,
                      message: translate(
                        "addVehicle.validation.reservedPriceMin"
                      ),
                    },
                  })}
                  className={`w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                  placeholder={translate(
                    "addVehicle.placeholders.reservedPrice"
                  )}
                />
                {errors.reservedPrice && (
                  <p className='mt-1 text-sm text-red-600'>
                    {errors.reservedPrice.message}
                  </p>
                )}
              </div>

              {/* Mileage*/}
              {/* <div>
                <label className='block text-sm font-medium text-white/70 mb-2'>
                  Mileage <span className='text-red-500'>*</span>
                </label>
                <input
                  type='number'
                  min='0'
                  step='0.01'
                  {...register("mileage", {
                    required: "Mileage is required",
                    min: { value: 0, message: "Price cannot be negative" },
                  })}
                  className='w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary'
                  placeholder='e.g., 25000'
                />
                {errors.mileage && (
                  <p className='mt-1 text-sm text-red-600'>
                    {errors.mileage.message}
                  </p>
                )}
              </div> */}
              {/* Market Value */}
              <div>
                <label className='block text-sm font-medium text-white/70 mb-2'>
                  {translate("addVehicle.labels.marketValue")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <input
                  type='number'
                  min='0'
                  step='0.01'
                  {...register("marketValue", {
                    required: translate(
                      "addVehicle.validation.marketValueRequired"
                    ),
                    min: {
                      value: 0,
                      message: translate(
                        "addVehicle.validation.marketValueMin"
                      ),
                    },
                  })}
                  className={`w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                  placeholder={translate("addVehicle.placeholders.marketValue")}
                />
                {errors.marketValue && (
                  <p className='mt-1 text-sm text-red-600'>
                    {errors.marketValue.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Vehicle Specifications */}
          <div className='bg-card rounded-lg shadow-sm border border-default p-6'>
            <h2 className='text-xl font-semibold text-white mb-6'>
              {translate("addVehicle.sections.specifications")}
            </h2>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* Body Type */}
              <div>
                <CustomDropdown
                  name='bodyTypeId'
                  label={translate("addVehicle.labels.bodyType")}
                  options={bodyTypes}
                  placeholder={translate(
                    "addVehicle.placeholders.selectBodyType"
                  )}
                />
              </div>

              {/* Fuel Type */}
              <div>
                <CustomDropdown
                  name='fuelTypeId'
                  label={translate("addVehicle.labels.fuelType")}
                  options={fuelTypes}
                  placeholder={translate(
                    "addVehicle.placeholders.selectFuelType"
                  )}
                />
              </div>

              {/*Specification*/}
              <div>
                <CustomDropdown
                  name='specificationId'
                  label={translate("addVehicle.labels.specification")}
                  options={specifications}
                  placeholder={translate(
                    "addVehicle.placeholders.selectSpecification"
                  )}
                />
              </div>

              {/* Transmission Type */}
              <div>
                <CustomDropdown
                  name='transmissionTypeId'
                  label={translate("addVehicle.labels.transmission")}
                  options={transmissionTypes}
                  placeholder={translate(
                    "addVehicle.placeholders.selectTransmission"
                  )}
                />
              </div>

              {/* Driveline Type */}
              <div>
                <CustomDropdown
                  name='driveLineTypeId'
                  label={translate("addVehicle.labels.driveline")}
                  options={drivelineTypes}
                  placeholder={translate(
                    "addVehicle.placeholders.selectDriveline"
                  )}
                />
              </div>

              {/* Primary Damage Type */}
              <div>
                <CustomDropdown
                  name='primaryDamageTypeId'
                  label={translate("addVehicle.labels.primaryDamageType")}
                  options={damageTypes}
                  placeholder={translate(
                    "addVehicle.placeholders.selectPrimaryDamageType"
                  )}
                  error={errors.primaryDamageTypeId}
                />
              </div>

              {/* Secondary Damage Type */}
              <div>
                <CustomDropdown
                  name='secondaryDamageTypeId'
                  label={translate("addVehicle.labels.secondaryDamageType")}
                  options={damageTypes}
                  placeholder={translate(
                    "addVehicle.placeholders.selectSecondaryDamageType"
                  )}
                  error={errors.secondaryDamageTypeId}
                />
              </div>

              {/* Seat Type */}
              <div>
                <CustomDropdown
                  name='seatTypeId'
                  label={translate("addVehicle.labels.seatType")}
                  options={seatTypes}
                  placeholder={translate(
                    "addVehicle.placeholders.selectSeatType"
                  )}
                  error={errors.seatTypeId}
                />
              </div>

              {/* Exterior Color */}
              <div>
                <CustomDropdown
                  name='exteriorColorId'
                  label={translate("addVehicle.labels.exteriorColor")}
                  options={exteriorColors}
                  placeholder={translate(
                    "addVehicle.placeholders.selectExteriorColor"
                  )}
                />
              </div>

              {/* Interior Color */}
              <div>
                <CustomDropdown
                  name='interiorColorId'
                  label={translate("addVehicle.labels.interiorColor")}
                  options={interiorColors}
                  placeholder={translate(
                    "addVehicle.placeholders.selectInteriorColor"
                  )}
                />
              </div>
            </div>
          </div>

          {/* Vehicle Images */}
          <div className='bg-card rounded-lg shadow-sm border border-default p-6'>
            <h2
              className={`text-xl font-semibold text-white mb-6 ${isRTL ? "text-right" : "text-left"
                }`}
            >
              {translate("addVehicle.sections.images")}
            </h2>

            <div className='space-y-4'>
              {/* Upload Area */}
              <div className='border-2 border-dashed border-default bg-page text-white rounded-lg p-6 text-center hover:border-primary transition-colors'>
                <input
                  type='file'
                  multiple
                  accept='image/*'
                  onChange={handleImageUpload}
                  className='hidden'
                  id='image-upload'
                />
                <label htmlFor='image-upload' className='cursor-pointer'>
                  <Upload className='w-12 h-12 text-gray-400 mx-auto mb-4' />
                  <p
                    className={`text-lg font-medium text-white/70 mb-2 ${isRTL ? "text-right" : "text-left"
                      }`}
                  >
                    {translate("addVehicle.images.uploadVehicleImages")}
                  </p>
                  <p
                    className={`text-sm text-gray-500 mb-4 ${isRTL ? "text-right" : "text-left"
                      }`}
                  >
                    {translate("addVehicle.images.uploadInstructions")}
                  </p>
                  <button
                    type='button'
                    className='inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors'
                    onClick={() =>
                      document.getElementById("image-upload").click()
                    }
                  >
                    {translate("addVehicle.buttons.chooseImages")}
                  </button>
                </label>
              </div>

              {/* Image Preview Grid */}
              {previewImages.length > 0 && (
                <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
                  {previewImages.map((item, index) => (
                    <div key={index} className='relative group'>
                      <img
                        src={item.preview}
                        alt={`Vehicle ${index + 1}`}
                        className='w-full h-32 object-cover rounded-lg'
                      />
                      <button
                        type='button'
                        onClick={() => removeImage(index)}
                        className='absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600'
                      >
                        <X className='w-4 h-4' />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Additional Information */}
          <div className='bg-card rounded-lg shadow-sm border border-default p-6'>
            <h2
              className={`text-xl font-semibold text-white mb-6 ${isRTL ? "text-right" : "text-left"
                }`}
            >
              {translate("addVehicle.additionalInfo.heading")}
            </h2>

            <div className='space-y-4'>
              {/* Description */}
              <div>
                <label
                  className={`block text-sm font-medium text-white/70 mb-2 ${isRTL ? "text-right" : "text-left"
                    }`}
                >
                  {translate("addVehicle.additionalInfo.vehicleDescription")}
                </label>
                <textarea
                  {...register("description")}
                  rows={4}
                  className={`w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                  placeholder={translate(
                    "addVehicle.additionalInfo.descriptionPlaceholder"
                  )}
                />
              </div>

              {/* Notes */}
              <div>
                <label
                  className={`block text-sm font-medium text-white/70 mb-2 ${isRTL ? "text-right" : "text-left"
                    }`}
                >
                  {translate("addVehicle.additionalInfo.additionalNotes")}
                </label>
                <textarea
                  {...register("notes")}
                  rows={3}
                  className={`w-full px-3 py-2 border border-default bg-page text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? "text-right" : "text-left"
                    }`}
                  placeholder={translate(
                    "addVehicle.additionalInfo.notesPlaceholder"
                  )}
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className={`flex ${isRTL ? "justify-start" : "justify-end"}`}>
            {/* <button
              type='button'
              // onClick={handleSubmit(handleSendForApproval)}
              // disabled={isApprovalSubmitted}
              className='inline-flex items-center gap-2 px-8 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 focus:ring-4 focus:ring-orange-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all'
            >
              Save & Send for Approval
            </button> */}

            <button
              type='submit'
              disabled={isLoading}
              className={`inline-flex items-center gap-2 px-8 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark focus:ring-4 focus:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all ${isRTL ? "flex-row-reverse" : ""
                }`}
            >
              {isLoading ? (
                <>
                  <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                  {isEditMode
                    ? translate("addVehicle.buttons.updatingVehicle")
                    : translate("addVehicle.buttons.addingVehicle")}
                </>
              ) : (
                <>
                  <Plus className='w-4 h-4' />
                  {isEditMode
                    ? translate("addVehicle.buttons.updateVehicle")
                    : translate("addVehicle.buttons.saveAndSendForApproval")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVehicles;
